<template>
  <div class="message-input-container">
    <!-- Ticket Fechado -->
    <div v-if="isTicketClosed" class="ticket-closed-message">
      <v-card class="pa-4" outlined>
        <div class="d-flex align-items-center justify-center">
          <v-icon color="grey" class="mr-2">lock</v-icon>
          <span class="text-muted">Este ticket está fechado. Não é possível adicionar novos comentários.</span>
        </div>
      </v-card>
    </div>

    <!-- Interface Normal (quando ticket não está fechado) -->
    <template v-else>
      <!-- Arquivos Selecionados -->
      <div v-if="fileParams.files.length > 0" class="selected-files">
      <div class="files-header">
        <div class="files-info">
          <v-icon size="18" color="primary">attach_file</v-icon>
          <span class="files-count">{{ quantityFilesSelected() }}</span>
        </div>
        <v-btn
          icon
          small
          color="error"
          @click="removeAllFiles"
          class="remove-files-btn"
        >
          <v-icon size="18">close</v-icon>
        </v-btn>
      </div>
      <div class="files-preview">
        <div
          v-for="(file, index) in fileParams.files"
          :key="index"
          class="file-preview"
        >
          <div class="file-icon">
            <v-icon size="16">description</v-icon>
          </div>
          <span class="file-name">{{ file.name }}</span>
          <v-btn
            icon
            x-small
            @click="removeFile(index)"
            class="remove-file-btn"
          >
            <v-icon size="12">close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Input de Mensagem -->
    <div class="input-wrapper">
      <div class="input-actions">
        <v-btn
          icon
          @click="onSelectFiles"
          class="attach-btn"
          color="grey darken-1"
        >
          <v-icon size="22">attach_file</v-icon>
        </v-btn>
      </div>

      <div class="text-input-container">
        <v-textarea
          v-model="message"
          placeholder="Digite sua mensagem..."
          auto-grow
          rows="1"
          row-height="24"
          hide-details
          class="message-textarea"
          @keydown.enter.prevent="handleEnterKey"
          @keyup.shift.enter="addNewLine"
          solo
          flat
        ></v-textarea>
      </div>

      <div class="send-action">
        <v-btn
          fab
          small
          @click="sendMessage"
          :disabled="!canSendMessage()"
          class="send-btn"
          :class="{ 'send-btn-active': canSendMessage() }"
          color="primary"
          elevation="2"
        >
          <v-icon size="20" color="white">send</v-icon>
        </v-btn>
      </div>
    </div>

      <!-- Input de arquivo oculto -->
      <input
        type="file"
        multiple
        style="display: none"
        ref="fileInput"
        accept="image/png,image/jpg,image/jpeg,.pdf,.txt,.doc,.docx,.xls,.xlsx,.csv"
        @change="onFilesSelected"
      >
    </template>
  </div>
</template>

<script>
import Flowit from '@/services/flowit';
import axios from 'axios';
import { getBackendBaseUrl } from '@/utils/urlHelper';

export default {
  name: 'MessageSender',
  data: () => ({
    show: false,
    message: '',
    fileParams: {
      files: [],
    },
  }),

  computed: {
    isTicketClosed() {
      const ticket = this.$store.state.tickets.ticket;
      return ticket && ticket.status === 'closed';
    }
  },

  methods: {
    sendMessage() {
      if (!this.canSendMessage()) return;

      this.$store.dispatch('tickets/postNewComment', {
        message: this.message,
        ticketId: this.$store.state.tickets.ticket.id,
        isFile: false,
        files: this.fileParams.files
      });
      this.clearMessage();
    },
    clearMessage() {
      this.message = '';
      this.fileParams.files = [];
    },
    canSendMessage() {
      return this.message.trim() || this.fileParams.files.length > 0;
    },
    handleEnterKey(event) {
      if (!event.shiftKey) {
        this.sendMessage();
      }
    },
    addNewLine() {
      // Shift+Enter adiciona nova linha (comportamento padrão do textarea)
    },
    quantityFilesSelected() {
      return `${this.fileParams.files.length} arquivo(s) selecionado(s)`;
    },
    removeAllFiles() {
      this.fileParams.files = [];
    },
    removeFile(index) {
      this.fileParams.files.splice(index, 1);
    },
    onSelectFiles() {
      this.$refs.fileInput.click();
    },
    async onFilesSelected(event) {
      const files = Array.from(this.$refs.fileInput.files);
      
      if (files.length === 0) return;

      // Validar tamanho total
      const totalSize = files.reduce((total, file) => total + file.size, 0);
      if (totalSize > 7000000) {
        alert('O tamanho total dos arquivos excedeu o limite de 7MB!');
        this.fileParams.files = [];
        return;
      }

      // Upload de cada arquivo
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);

          // Usar o helper para obter a URL base correta
          const backendUrl = getBackendBaseUrl();
          const baseURL = backendUrl ? `${backendUrl}/api/v1` : '/api/v1';

          const response = await axios.post(`${baseURL}/tickets/upload_file`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': Flowit.token.get()
            }
          });

          if (response.data.success) {
            // Criar comentário com o arquivo
            this.$store.dispatch('tickets/postNewComment', {
              message: response.data.file_url,
              ticketId: this.$store.state.tickets.ticket.id,
              isFile: true,
              files: [{
                url: response.data.file_url,
                name: response.data.filename,
                size: response.data.size
              }]
            });
          }
        } catch (error) {
          console.error('Erro no upload do arquivo:', error);
          const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Erro ao fazer upload do arquivo';
          alert(`Erro: ${errorMessage}`);
        }
      }

      // Limpar input
      this.$refs.fileInput.value = '';
    },
  },
};
</script>

<style scoped>
/* Container Principal */
.message-input-container {
  background: white;
  border-radius: 0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

/* Arquivos Selecionados */
.selected-files {
  padding: 16px 20px 12px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.files-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.files-count {
  font-size: 13px;
  color: #6c757d;
  font-weight: 600;
}

.remove-files-btn {
  background: rgba(220, 53, 69, 0.1) !important;
  border: 1px solid rgba(220, 53, 69, 0.2);
  transition: all 0.2s ease;
}

.remove-files-btn:hover {
  background: rgba(220, 53, 69, 0.15) !important;
  transform: scale(1.05);
}

.files-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-preview {
  display: flex;
  align-items: center;
  background: white;
  padding: 8px 12px;
  border-radius: 16px;
  border: 1px solid #dee2e6;
  font-size: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  gap: 8px;
}

.file-preview:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.file-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: #e3f2fd;
  border-radius: 50%;
  flex-shrink: 0;
}

.file-name {
  flex: 1;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #495057;
}

.remove-file-btn {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  background: rgba(220, 53, 69, 0.1) !important;
  transition: all 0.2s ease;
}

.remove-file-btn:hover {
  background: rgba(220, 53, 69, 0.2) !important;
  transform: scale(1.1);
}

/* Input de Mensagem */
.input-wrapper {
  display: flex;
  align-items: flex-end;
  padding: 20px;
  gap: 16px;
  background: white;
}

.input-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attach-btn {
  background: rgba(108, 117, 125, 0.1) !important;
  border: 1px solid rgba(108, 117, 125, 0.2);
  transition: all 0.3s ease;
  width: 44px !important;
  height: 44px !important;
}

.attach-btn:hover {
  background: rgba(108, 117, 125, 0.15) !important;
  transform: scale(1.1);
}

.text-input-container {
  flex: 1;
  background: #f8f9fa;
  border-radius: 24px;
  padding: 8px 20px;
  min-height: 44px;
  display: flex;
  align-items: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.text-input-container:focus-within {
  border-color: var(--v-primary-base, #1976d2);
  box-shadow: 0 0 0 3px var(--v-primary-lighten4, rgba(25, 118, 210, 0.1));
  background: white;
}

.message-textarea {
  width: 100%;
}

.message-textarea :deep(.v-input__slot) {
  background: transparent !important;
  box-shadow: none !important;
  min-height: 28px;
  padding: 0;
}

.message-textarea :deep(.v-text-field__slot) {
  min-height: 28px;
  padding: 0;
}

.message-textarea :deep(textarea) {
  font-size: 15px;
  line-height: 1.5;
  resize: none;
  max-height: 120px;
  padding: 0;
  color: #495057;
}

.message-textarea :deep(textarea::placeholder) {
  color: #adb5bd;
  font-weight: 400;
}

.send-action {
  display: flex;
  align-items: center;
}

.send-btn {
  width: 44px !important;
  height: 44px !important;
  transition: all 0.3s ease;
  background: var(--v-primary-base, linear-gradient(135deg, #1976d2 0%, #1565c0 100%)) !important;
  box-shadow: 0 2px 8px var(--v-primary-lighten2, rgba(25, 118, 210, 0.3));
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.1);
  box-shadow: 0 4px 16px var(--v-primary-lighten1, rgba(25, 118, 210, 0.4));
}

.send-btn:disabled {
  opacity: 0.5;
  transform: none;
  box-shadow: none;
  background: #dee2e6 !important;
}

.send-btn-active {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 2px 8px var(--v-primary-lighten2, rgba(25, 118, 210, 0.3));
  }
  50% {
    box-shadow: 0 4px 16px var(--v-primary-lighten1, rgba(25, 118, 210, 0.5));
  }
  100% {
    box-shadow: 0 2px 8px var(--v-primary-lighten2, rgba(25, 118, 210, 0.3));
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .input-wrapper {
    padding: 16px 12px;
    gap: 12px;
  }

  .selected-files {
    padding: 12px 16px 8px 16px;
  }

  .attach-btn {
    width: 40px !important;
    height: 40px !important;
  }

  .text-input-container {
    padding: 6px 16px;
    min-height: 40px;
    border-radius: 20px;
  }

  .message-textarea :deep(textarea) {
    font-size: 14px;
  }

  .send-btn {
    width: 40px !important;
    height: 40px !important;
  }

  .file-name {
    max-width: 100px;
  }

  .file-preview {
    padding: 6px 10px;
    font-size: 11px;
  }

  .files-count {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .input-wrapper {
    padding: 12px 8px;
    gap: 8px;
  }

  .selected-files {
    padding: 8px 12px;
  }

  .attach-btn {
    width: 36px !important;
    height: 36px !important;
  }

  .text-input-container {
    padding: 4px 12px;
    min-height: 36px;
    border-radius: 18px;
  }

  .send-btn {
    width: 36px !important;
    height: 36px !important;
  }

  .file-name {
    max-width: 80px;
  }

  .file-preview {
    padding: 4px 8px;
    border-radius: 12px;
  }

  .file-icon {
    width: 20px;
    height: 20px;
  }
}
</style>
